import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import { and, not, or } from 'tio-ui/utilities';
import { htmlSafe } from '@ember/template';
import { t } from 'ember-intl';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { formatCurrency, formatShortDate } from 'tio-ui/format';
import lastFourDigits from 'tio-common/helpers/last-four-digits';
import StatePill from 'tio-employee/components/tas/state-pill';
import { PhBooks, PhStudent } from 'ember-phosphor-icons';
import type { AmountInfo } from 'tio-employee/utils/tas/activity-helpers';
import type { ComponentLike } from '@glint/template';

// The amount chips lean on status colors, not brand purple (Brand Updates 2026 Disbursed Via
// `423:1391`): requested neutral, approved/reserved blue, pending/used amber, paid green,
// repayment red.
const AMOUNT_STATUS_DOT_CLASSES: Record<string, string> = {
  'tuition_assistance.reimagined.index.application_amount_requested': 'tio-bg-neutral',
  'tuition_assistance.reimagined.index.application_amount_approved': 'tio-bg-info',
  'tuition_assistance.reimagined.index.application_amount_reserved': 'tio-bg-info',
  'tuition_assistance.reimagined.index.application_amount_pending': 'tio-bg-warning',
  'tuition_assistance.reimagined.index.application_amount_used': 'tio-bg-warning',
  'tuition_assistance.reimagined.index.application_amount_repayment': 'tio-bg-danger',
  'tuition_assistance.reimagined.index.application_amount_paid': 'tio-bg-success',
};

const amountStatusDotClass = (labelKey: string): string =>
  AMOUNT_STATUS_DOT_CLASSES[labelKey] ?? 'tio-bg-neutral';

interface ActivityCardSignature {
  Args: {
    /** Route to link to (e.g. "authenticated.tas.applications.show") */
    route: string;
    /** Model param for the route (the record id) */
    model: string | null;
    /** Display name for the card heading */
    name: string;
    /** Raw state string for the StatePill */
    state: string | undefined;
    /** Short ID for "ID# xxNNNN" display */
    shortId?: string | number | null;
    /** Program name to show in metadata line */
    programName?: string | null;
    /** Date string for "last action" display */
    updatedAt?: string | null;
    /** Institution name (extra metadata line, typically for program instances) */
    institution?: string | null;
    /** Amount info for reimbursement/course-cash metric pills */
    amountInfo?: AmountInfo | null;
    /** Card variant: "standalone" renders with border/shadow, "nested" renders as a flat row */
    variant?: 'standalone' | 'nested';
    /** Icon kind: "application" shows PhBooks, "program" shows PhStudent */
    iconKind?: 'application' | 'program';
    /** Optional payment state string (e.g. REFUND_REQUESTED) for a second badge */
    paymentState?: string | null;
    /** Number of courses (or credits for credit-based programs) to show as a metric pill */
    courseCount?: number | null;
    /** Whether the application is credit-based (swaps "Courses" label for "Credits") */
    isCreditBased?: boolean;
  };
  Blocks: {
    /** Optional block for custom right-side metrics (overrides default amountInfo pills) */
    metrics?: [];
  };
  Element: HTMLAnchorElement;
}

/** Links application/program activity to its detail route; the metrics block overrides default amount content. */
export default class ActivityCard extends Component<ActivityCardSignature> {
  get isNested() {
    return this.args.variant === 'nested';
  }

  get containerClass() {
    if (this.isNested) {
      return 'tio-activity-card tio-activity-card--nested';
    }
    return 'tio-activity-card tio-card tio-card--radius-card tio-card--elevation-card tio-card--padding-sm tio-elevation-card-hover';
  }

  get Icon(): ComponentLike<{ Element: SVGElement }> {
    return this.args.iconKind === 'program' ? PhStudent : PhBooks;
  }

  <template>
    <LinkTo @route={{@route}} @model={{@model}} class={{this.containerClass}}>
      <div class="tio-row">
        <div class="tio-row-center h-12 w-12 shrink-0 rounded-lg tio-bg-brand-soft">
          {{#let this.Icon as |Icon|}}
            <Icon class="h-7 w-7 tio-fg-brand" />
          {{/let}}
        </div>
        <div class="tio-col-extra-tight tio-fill-min">
          <div class="tio-row-tight flex-wrap">
            <Heading @level={{3}} @size="base" class="truncate">{{@name}}</Heading>
            <StatePill @state={{@state}} @size="sm" />
            {{#if @paymentState}}
              <StatePill @state={{@paymentState}} @size="sm" />
            {{/if}}
          </div>
          <Text @variant="muted" @size="sm">
            {{#if @shortId}}
              <span>{{t
                  "tuition_assistance.reimagined.dashboard.activity_id"
                  id=(lastFourDigits @shortId)
                }}</span>
            {{/if}}
            {{#if @programName}}
              {{#if @shortId}}<span class="mx-1">&bull;</span>{{/if}}
              <span>{{@programName}}</span>
            {{/if}}
            {{#if (and (not (eq @courseCount null)) (not (eq @courseCount undefined)))}}
              {{#if (or @shortId @programName)}}<span class="mx-1">&bull;</span>{{/if}}
              <span>{{if
                  @isCreditBased
                  (t "tuition_assistance.reimagined.index.credit_count" count=@courseCount)
                  (t "tuition_assistance.reimagined.index.course_count" count=@courseCount)
                }}</span>
            {{/if}}
            {{#if @updatedAt}}
              {{#if (this.hasPrecedingMeta @shortId @programName @courseCount)}}
                <span class="mx-1">&bull;</span>
              {{/if}}
              <span>{{t
                  "tuition_assistance.reimagined.dashboard.activity_last_action"
                  date=(formatShortDate @updatedAt)
                }}</span>
            {{/if}}
          </Text>
          {{#if @institution}}
            <Text @variant="muted" @size="sm">
              {{@institution}}
            </Text>
          {{/if}}
        </div>
        {{#if (has-block "metrics")}}
          {{yield to="metrics"}}
        {{else if @amountInfo}}
          <div class="hidden shrink-0 gap-2 md:flex items-center">
            {{#if (gt @amountInfo.reimbursementAmount 0)}}
              <div class="tio-disbursement-chip" data-test-amount-chip="reimbursement">
                <div>
                  <div class="tio-disbursement-chip__type">
                    {{t "tuition_assistance.reimagined.dashboard.metric_reimbursement"}}
                  </div>
                  <div class="tio-disbursement-chip__amount">
                    {{formatCurrency @amountInfo.reimbursementAmount}}
                  </div>
                </div>
                <div class="tio-disbursement-chip__status">
                  {{t @amountInfo.reimbursementLabelKey}}
                  <span
                    class="tio-disbursement-chip__dot
                      {{amountStatusDotClass @amountInfo.reimbursementLabelKey}}"
                    data-test-amount-chip-dot
                  ></span>
                </div>
              </div>
            {{/if}}
            {{#if @amountInfo.hasCourseCash}}
              <div class="tio-disbursement-chip" data-test-amount-chip="course-cash">
                <div>
                  <div class="tio-disbursement-chip__type app-rich-text">
                    {{htmlSafe (t "tuition_assistance.reimagined.dashboard.metric_course_cash")}}
                  </div>
                  <div class="tio-disbursement-chip__amount">
                    {{formatCurrency @amountInfo.courseCashAmount}}
                  </div>
                </div>
                <div class="tio-disbursement-chip__status">
                  {{t @amountInfo.courseCashLabelKey}}
                  <span
                    class="tio-disbursement-chip__dot
                      {{amountStatusDotClass @amountInfo.courseCashLabelKey}}"
                    data-test-amount-chip-dot
                  ></span>
                </div>
              </div>
            {{/if}}
          </div>
        {{/if}}
      </div>
    </LinkTo>
  </template>

  /**
   * Returns true if there's a shortId or programName preceding the date,
   * meaning we need a bullet separator before the date.
   */
  hasPrecedingMeta = (
    shortId: string | number | null | undefined,
    programName: string | null | undefined,
    courseCount: number | null | undefined
  ) => {
    return !!shortId || !!programName || (courseCount !== null && courseCount !== undefined);
  };
}
